/**
 * OutputSectionsSection — admin-authored rich-text sections after the last
 * estimate tables (universal defaults, replaced / hidden per retailer,
 * translated server-side). Shared by EstimateResults (wizard) and SharedEstimate.
 *
 * Screen modes: always visible, collapsible (open / closed) or hidden. A
 * collapsed section stays in the DOM and is only hidden by a class, so the
 * print stylesheet can show it in full (the printed page is this same DOM).
 * Page controls ride along as classes / a CSS variable for @media print; the
 * PDF applies them itself (pdfSections.ts).
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, FileText } from 'lucide-react';
import type { OutputSection } from '../../config/outputSections';
import { getOutputSections } from '../../data/productTable';
import { sanitizeSectionHtml } from '../../utils/sectionHtml';
import './OutputSections.css';

interface Props {
  /** Defaults to the sections loaded with the product payload. */
  sections?: OutputSection[];
}

export const OutputSectionsSection: React.FC<Props> = ({ sections }) => {
  const list = (sections ?? getOutputSections()).filter((s) => s.content.html.trim() !== '');
  if (list.length === 0) return null;
  return (
    <>
      {list.map((s) => <OutputSectionCard key={s.id} section={s} />)}
    </>
  );
};

/** Printable body height of a Letter sheet with the print stylesheet's 0.75in margins. */
const PRINT_BODY_IN = 9.5;
const PRINT_COLUMN_IN = 7; // 8.5in less the margins
/** Same floor as the PDF (pdfSections.FIT_MIN_SCALE). */
const PRINT_MIN_ZOOM = 0.5;

/**
 * Max pages, for print: right before printing, measure the section at the
 * print column width and set a CSS `zoom` that scales the WHOLE section down
 * until it fits in N sheets — the browser reflows under zoom, so nothing is
 * clipped. A collapsed section is measured display:block (print shows it in
 * full). Approximate by nature (browser print margins vary); the PDF is exact.
 */
function fitToPagesForPrint(body: HTMLElement, maxPages: number): () => void {
  const html = body.querySelector<HTMLElement>('.os__html');
  if (!html) return () => {};
  const wasHidden = getComputedStyle(body).display === 'none';
  if (wasHidden) body.style.display = 'block';
  html.style.removeProperty('zoom');
  html.style.width = `${PRINT_COLUMN_IN}in`;
  const limitPx = maxPages * PRINT_BODY_IN * 96;
  let zoom = 1;
  // A couple of passes: zoom reflows (fewer line wraps), so re-measure and refine.
  for (let pass = 0; pass < 3; pass++) {
    const h = html.scrollHeight * zoom; // scrollHeight is in the element's own (zoomed) px
    if (h <= limitPx * 0.995) { if (zoom >= 1) break; zoom = Math.min(1, zoom * (limitPx / h)); }
    else zoom = zoom * (limitPx / h);
    zoom = Math.max(PRINT_MIN_ZOOM, Math.min(1, zoom));
    html.style.setProperty('zoom', String(zoom));
    if (zoom === PRINT_MIN_ZOOM) break;
  }
  html.style.removeProperty('width');
  if (wasHidden) body.style.removeProperty('display');
  if (zoom >= 1) html.style.removeProperty('zoom');
  return () => { html.style.removeProperty('zoom'); };
}

const OutputSectionCard: React.FC<{ section: OutputSection }> = ({ section: s }) => {
  const { t } = useTranslation('results');
  const { screen, print } = s.content.display;
  const collapsible = screen === 'collapsibleOpen' || screen === 'collapsibleClosed';
  const [open, setOpen] = useState(screen !== 'collapsibleClosed');
  const html = useMemo(() => sanitizeSectionHtml(s.content.html), [s.content.html]);
  const bodyRef = useRef<HTMLDivElement>(null);

  const maxPages = print ? s.content.page.maxPages : null;
  useEffect(() => {
    if (!maxPages) return undefined;
    let undo: (() => void) | null = null;
    const before = () => { if (bodyRef.current) undo = fitToPagesForPrint(bodyRef.current, maxPages); };
    const after = () => { undo?.(); undo = null; };
    window.addEventListener('beforeprint', before);
    window.addEventListener('afterprint', after);
    return () => {
      window.removeEventListener('beforeprint', before);
      window.removeEventListener('afterprint', after);
      undo?.();
    };
  }, [maxPages]);

  if (screen === 'hidden' && !print) return null;

  const classes = ['output-section', 'os'];
  if (screen === 'hidden') classes.push('d-none', 'd-print-block');
  else if (!print) classes.push('d-print-none');
  if (collapsible) classes.push('os--collapsible');
  if (collapsible && !open) classes.push('is-collapsed');
  if (s.content.page.startOnNewPage) classes.push('os--break-before');
  if (s.content.page.breakAfter) classes.push('os--break-after');

  const bodyId = `os-body-${s.key}`;

  return (
    <section id={`os-${s.key}`} className={classes.join(' ')}>
      <div className="os__heading">
        <h2 className="section-title os__title">
          {collapsible ? (
            <button
              type="button"
              className="os__toggle"
              aria-expanded={open}
              aria-controls={bodyId}
              onClick={() => setOpen((o) => !o)}
            >
              <FileText size={22} className="section-title-icon" aria-hidden />
              <span>{s.title}</span>
              <ChevronDown size={20} className="os__chevron" aria-hidden />
              <span className="visually-hidden os__sr">{t(open ? 'outputSections.collapse' : 'outputSections.expand')}</span>
            </button>
          ) : (
            <>
              <FileText size={22} className="section-title-icon" aria-hidden />
              <span>{s.title}</span>
            </>
          )}
        </h2>
      </div>
      <div id={bodyId} className="os__body" ref={bodyRef}>
        <div className="os__html" dangerouslySetInnerHTML={{ __html: html }} />
      </div>
    </section>
  );
};

export default OutputSectionsSection;
